import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Skeleton } from "@/components/ui/skeleton";
import { useProductDetailState } from "@/hooks/useProductDetailState";
import { ProductHeroSection } from "@/components/product/ProductHeroSection";
import { ProductPurchaseCard } from "@/components/product/ProductPurchaseCard";
import { ProductContentTabs } from "@/components/product/ProductContentTabs";
import { ProductGateOverlay } from "@/components/product/ProductGateOverlay";
import { ReEnrichmentModal } from "@/components/admin/ReEnrichmentModal";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Share2, Link as LinkIcon, Mail, Trash2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { EditableText } from "@/components/admin/EditableText";
import { EditablePrice } from "@/components/admin/EditablePrice";
import { toast } from "sonner";
import { PublishButton } from "@/components/admin/PublishButton";
import { VendorWorkflowButtons } from "@/components/admin/VendorWorkflowButtons";
import { isFeatureEnabled } from "@/lib/featureFlags";
import { getProPricing } from "@/lib/proPricing";

const ProductDetail = () => {
  const {
    slug,
    user,
    isEditor,
    membership,
    isEditMode,
    product,
    isLoading,
    promoCodes,
    hasPromo,
    isService,
    isCourse,
    hasAffiliatePlans,
    purchaseModalOpen,
    setPurchaseModalOpen,
    selectedImage,
    setSelectedImage,
    demoModalOpen,
    setDemoModalOpen,
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab,
    enrichModalOpen,
    setEnrichModalOpen,
    showDeleteDialog,
    setShowDeleteDialog,
    headerVisible,
    showGate,
    setShowGate,
    gateShownAt,
    handleUpdateField,
    handleUpdateQA,
    handleSavePlans,
    handleBuyNow,
    handleSelectPlan,
    handleDelete,
    isDeleting,
    isGenerating,
    generateQA,
    openBooking,
    BookingModal,
    navigate,
  } = useProductDetailState();

  const productUrl = typeof window !== 'undefined' ? window.location.href : '';
  const pro = product ? getProPricing(product, membership) : null;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(productUrl);
    toast.success('Link copied to clipboard!');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(`Check out ${product?.title || 'this product'}`);
    const body = encodeURIComponent(`I thought you might be interested in this: ${productUrl}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleShareLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(productUrl)}`, '_blank');
  };

  const handleShareTwitter = () => {
    const text = encodeURIComponent(`Check out ${product?.title || 'this product'}`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(productUrl)}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1">
          <div className="container mx-auto max-w-6xl px-4 py-8">
            <Skeleton className="h-12 w-3/4 mb-4" />
            <Skeleton className="h-6 w-1/2 mb-8" />
            <Skeleton className="h-96 w-full mb-8" />
            <Skeleton className="h-64 w-full" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1">
          <div className="container mx-auto max-w-6xl px-4 py-8">
            <h1 className="text-2xl font-bold mb-4">Product Not Found</h1>
            <p className="text-muted-foreground mb-4">The product you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/browse')}>Browse Products</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header forceVisible={headerVisible} />
      
      <main className="flex-1">
        {/* Sticky Price Bar */}
        <div className={`sticky z-50 bg-background/95 backdrop-blur-md border-b shadow-lg transition-all duration-300 ease-in-out ${headerVisible ? 'top-[100px]' : 'top-0'}`}>
          <div className="container mx-auto max-w-6xl px-4">
            <div className="flex items-center justify-between h-16 md:h-20">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10 md:h-12 md:w-12 ring-2 ring-primary/20" key={(product as any).profile_image || product.image}>
                  <AvatarImage src={(product as any).profile_image || product.image} alt={product.title} />
                  <AvatarFallback>{product.title[0]}</AvatarFallback>
                </Avatar>
                <div className="max-w-[200px] md:max-w-none">
                  <div className="font-bold text-xs md:text-sm truncate">
                    <EditableText value={product.title} onSave={value => handleUpdateField('title', value)} className="inline" />
                  </div>
                  <div className="hidden md:block text-xs text-muted-foreground truncate">{product.subtitle.slice(0, 40)}...</div>
                </div>
              </div>
              
              <div className="hidden md:flex items-center gap-6">
                {isService ? (
                  <>
                    <button onClick={() => setDemoModalOpen(true)} className="text-sm font-medium hover:text-primary">Demo</button>
                    <a href="#roi" className="text-sm font-medium hover:text-primary">ROI</a>
                    <a href="#setup" className="text-sm font-medium hover:text-primary">Setup</a>
                    <a href="#reviews" className="text-sm font-medium hover:text-primary">Reviews</a>
                    <button onClick={() => {
                      setActiveTab('faqs');
                      setTimeout(() => {
                        document.getElementById('faqs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }} className="text-sm font-medium hover:text-primary">
                      FAQs
                    </button>
                    <a href="#pricing" className="text-sm font-medium hover:text-primary">Plans</a>
                  </>
                ) : (
                  <>
                    <a href="#pricing" className="text-sm font-medium hover:text-primary">Pricing</a>
                    <button onClick={() => {
                      setActiveTab('faqs');
                      setTimeout(() => {
                        document.getElementById('faqs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }} className="text-sm font-medium hover:text-primary">
                      FAQs
                    </button>
                    <a href="#reviews" className="text-sm font-medium hover:text-primary">Reviews</a>
                  </>
                )}
              </div>

              <div className="flex items-center gap-2 md:gap-4">
                {isEditor && isEditMode && isFeatureEnabled('productEnrichment') && (
                  <Button variant="outline" size="sm" onClick={() => setEnrichModalOpen(true)} className="h-9">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Re-Enrich
                  </Button>
                )}
                
                {isEditor && isEditMode && (
                  <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" className="h-9">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Product?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete "{product?.title}" and all associated data. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDelete}
                          disabled={isDeleting}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {isDeleting ? 'Deleting...' : 'Delete Product'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                
                <div className="hidden sm:block">
                  <div className="text-xs text-muted-foreground mb-0.5">{isService ? 'Starting at' : 'Price'}</div>
                  <div className="flex items-center gap-1.5">
                    <div className="text-xl font-bold">
                      <EditablePrice value={product.price} onSave={value => handleUpdateField('price', value)} />
                    </div>
                    {product.price && product.price > 0 && <span className="text-xs text-muted-foreground">/{product.price_duration || 'mo'}</span>}
                  </div>
                </div>
                <Button className="font-semibold text-sm md:text-base h-9 md:h-11 shadow-md hover:shadow-lg transition-shadow" onClick={handleBuyNow}>
                  {hasAffiliatePlans ? 'View Pricing' : isCourse && (product as any)?.access_url ? 'Enroll' : isService && product?.booking_url ? 'Book a Call' : 'Buy now'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Breadcrumb Navigation */}
        <div className="container mx-auto max-w-6xl px-4 pt-4 pb-2">
          <div className="flex items-center justify-between gap-2 sm:gap-4">
            <div className="flex-1 min-w-0">
              <Breadcrumb>
                <BreadcrumbList className="flex-wrap-none text-xs sm:text-sm">
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/">Home</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  {product.categories && product.categories.length > 0 && (
                    <>
                      <BreadcrumbSeparator className="hidden sm:inline" />
                      <BreadcrumbItem className="hidden sm:inline">
                        <BreadcrumbLink asChild>
                          <Link to={`/browse?category=${encodeURIComponent(product.categories[0])}`}>
                            {product.categories[0]}
                          </Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                    </>
                  )}
                  <BreadcrumbSeparator />
                  <BreadcrumbItem className="min-w-0">
                    <BreadcrumbPage className="truncate max-w-[150px] sm:max-w-none">{product.title}</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            
            <div className="flex-shrink-0 flex items-center gap-1.5 sm:gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-[10px] sm:text-xs gap-1 sm:gap-1.5 px-2 sm:px-3">
                    <Share2 className="h-3.5 w-3.5" />
                    <span>Share</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64" align="end">
                  <div className="space-y-2">
                    <h4 className="font-semibold text-sm mb-3">Share this {isService ? 'service' : 'product'}</h4>
                    <Button variant="outline" className="w-full justify-start" onClick={handleCopyLink}>
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Copy Link
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={handleShareEmail}>
                      <Mail className="h-4 w-4 mr-2" />
                      Share via Email
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={handleShareLinkedIn}>
                      <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                      Share on LinkedIn
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={handleShareTwitter}>
                      <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                      Share on X
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {/* Hero Tagline */}
        {product.subtitle && (
          <div className="container mx-auto max-w-6xl px-4 pb-1 pt-1">
            <h2 className="text-xl md:text-2xl font-bold text-left text-foreground">
              <EditableText 
                value={product.subtitle} 
                onSave={value => handleUpdateField('subtitle', value)} 
                as="span" 
              />
            </h2>
          </div>
        )}

        {/* Hero Section */}
        <section className="bg-background py-2 md:py-4">
          <div className="container mx-auto max-w-6xl px-4">
            <div className="grid lg:grid-cols-[2fr,0.75fr] gap-8 items-start">
              <ProductHeroSection
                product={product}
                selectedImage={selectedImage}
                isEditMode={isEditMode}
                onImageSelect={setSelectedImage}
                onUpdateField={handleUpdateField}
              />
              
              <div className="lg:sticky lg:top-16">
                <ProductPurchaseCard
                  product={product}
                  isService={isService}
                  isCourse={isCourse}
                  hasAffiliatePlans={hasAffiliatePlans}
                  pro={pro}
                  isEditor={isEditor}
                  isEditMode={isEditMode}
                  onUpdateField={handleUpdateField}
                  onBuyNow={handleBuyNow}
                  onOpenBooking={openBooking}
                  onOpenDemo={() => setDemoModalOpen(true)}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 max-w-6xl">
            <ProductContentTabs
              product={product}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onUpdateQA={handleUpdateQA}
              onGenerateQA={() => slug && generateQA({ productSlug: slug })}
              isGenerating={isGenerating}
              isEditor={isEditor}
              isEditMode={isEditMode}
            />
          </div>
        </section>
      </main>

      <Footer />
      
      {/* Modals */}
      <BookingModal />
      
      <ReEnrichmentModal
        open={enrichModalOpen}
        onOpenChange={setEnrichModalOpen}
        productId={slug || ''}
      />

      <ProductGateOverlay
        open={showGate}
        productId={slug}
        gateShownAt={gateShownAt}
        onSignupSuccess={() => setShowGate(false)}
      />

      {isEditor && isEditMode && (
        <div className="fixed bottom-4 right-4 z-50 flex gap-2">
          <PublishButton 
            productId={product.id} 
            isPublished={product.is_published || false}
          />
          <VendorWorkflowButtons 
            productId={product.id}
            productSlug={product.slug}
            status={(product as any).status || 'draft'}
            vendorEmail={(product as any).vendor_email}
            vendorReviewToken={(product as any).vendor_review_token}
          />
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
